import { PencilIcon } from "lucide-react";
import { useState } from "react";
import { SubmitHandler } from "react-hook-form";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useMutation } from "urql";

import { TournamentStageForm } from "@/components/forms/TournamentStageForm";
import {
    TournamentStageFormInput,
    zTournamentStageFormInput,
} from "@/components/forms/validation/tournament-stage";
import { buttonVariants } from "@/components/ui/button";
import { Button } from "@/components/ui/button";
import { DestructiveConfirmationAlertDialog } from "@/components/ui/destructive-confirmation-alert-dialog";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { graphql } from "@/gen";
import { TournamentStageT } from "@/routes/tournament-stage/queries";
import { cn } from "@/utils";

const updateTournamentStageMutation = graphql(`
    mutation updateTournamentStage($input: UpdateTournamentStageInput!) {
        updateTournamentStage(input: $input) {
            __typename
            id
            ...useTournamentStage_TournamentStage
        }
    }
`);

const deleteTournamentStageMutation = graphql(`
    mutation deleteTournamentStage($tournamentStageId: UUID!) {
        deleteTournamentStage(tournamentStageId: $tournamentStageId)
    }
`);

export const TournamentStageEditDialog = () => {
    const [_res, updateTournamentStage] = useMutation(updateTournamentStageMutation);
    const [_resDelete, deleteTournamentStage] = useMutation(deleteTournamentStageMutation);
    const { tournamentStage } = useOutletContext<{ tournamentStage: TournamentStageT }>();
    const [isOpen, setIsOpen] = useState(false);
    const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
    const navigate = useNavigate();
    const { tournamentId } = useParams<{ tournamentId: string }>();

    const onSubmit: SubmitHandler<TournamentStageFormInput> = async input => {
        const result = await updateTournamentStage({
            input: {
                id: tournamentStage.id,
                title: input.title,
                description: input.description,
                kind: input.kind,
                order: input.order,
                trialFormat: input.trialFormat,
                tieBreakers: input.tieBreakers,
                eliminationSettings: input.eliminationSettings,
            },
        });

        if (result.error) {
            toast.error("Unable to update tournament stage", { description: result.error.message });
            for (const error of result.error.graphQLErrors) {
                // @ts-expect-error typed as string from the API
                form.setError(error.name, { message: error.message });
            }
        } else {
            toast.success("Tournament stage was updated");
            setIsOpen(false);
        }
    };

    const defaultValues = zTournamentStageFormInput.parse(tournamentStage);

    const handleDelete = async () => {
        const res = await deleteTournamentStage({ tournamentStageId: tournamentStage.id });
        if (res.error) {
            toast.error("Unable to delete tournament stage", { description: res.error.message });
        } else {
            toast.success("Tournament stage was deleted");
            setIsOpen(false);
            if (tournamentId) {
                navigate(`/tournaments/${tournamentId}`);
            } else {
                navigate(`/tournaments`);
            }
        }
    };

    const onDelete = () => {
        setDeleteAlertOpen(true);
    };

    return (
        <>
            <Dialog open={isOpen} onOpenChange={() => setIsOpen(!isOpen)}>
                <DialogTrigger className={cn(buttonVariants())}>
                    <PencilIcon />
                </DialogTrigger>
                <DialogContent
                    className="max-h-[80vh] w-full max-w-3xl overflow-y-auto"
                    aria-describedby={undefined}
                >
                    <DialogHeader>
                        <div className="mr-4 flex items-center justify-between">
                            <DialogTitle>Edit stage tournament</DialogTitle>
                        </div>
                    </DialogHeader>
                    <TournamentStageForm onSubmit={onSubmit} defaultValues={defaultValues} />
                    <div className="mt-4 flex justify-end">
                        <Button
                            type="button"
                            variant="outline"
                            className="border-red-600 text-red-600"
                            onClick={onDelete}
                        >
                            Delete stage
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <DestructiveConfirmationAlertDialog
                open={deleteAlertOpen}
                onOpenChange={setDeleteAlertOpen}
                onConfirm={handleDelete}
                title="Delete Tournament Stage"
                description="Are you sure you want to delete this stage? This action cannot be undone."
            />
        </>
    );
};
